import React, { useMemo } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Loader2,
  Reply,
  Share,
  Tag,
  Download,
  Eye,
  EyeOff,
  AlertTriangle,
  Maximize,
  ArrowLeft,
} from 'lucide-react';
import { GmailAttachment, GmailMessage } from '../../types/api';

interface MessageViewerProps {
  messageId: string | null;
  message?: GmailMessage | null;
  isLoading: boolean;
  error?: unknown;
  isTogglingRead?: boolean;
  onReply?: (message: GmailMessage) => void;
  onForward?: (message: GmailMessage) => void;
  onToggleRead?: (message: GmailMessage, unread: boolean) => Promise<void> | void;
  onEnterFullscreen?: (message: GmailMessage) => void;
  onBack?: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: ${props => props.theme.colors.background};
  border-left: 1px solid ${props => props.theme.colors.border};
`;

const Placeholder = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing[3]};
  color: ${props => props.theme.colors.textSecondary};
  text-align: center;
  padding: ${props => props.theme.spacing[6]};

  svg {
    width: 48px;
    height: 48px;
    opacity: 0.4;
  }
`;

const Header = styled.div`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[1]};
  background: ${props => props.theme.colors.backgroundSecondary};
`;

const Subject = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[2]};
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};

  span {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing[1]};
  }
`;

const Badge = styled.span<{ $variant?: 'default' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  font-size: 11px;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
  border-radius: ${props => props.theme.borders.radius.sm};
  background: ${props =>
    props.$variant === 'warning' ? '#facc1515' : props.theme.colors.backgroundTertiary};
  border: 1px solid ${props =>
    props.$variant === 'warning' ? '#facc1580' : props.theme.colors.border};
  color: ${props =>
    props.$variant === 'warning' ? '#a16207' : props.theme.colors.textSecondary};
`;

const ActionBar = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[1]};
  margin-top: ${props => props.theme.spacing[2]};
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'outline' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing[2]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: 13px;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${props => props.$variant === 'primary'
    ? props.theme.colors.primary
    : props.theme.colors.border};
  background: ${props => props.$variant === 'primary'
    ? props.theme.colors.primary
    : 'transparent'};
  color: ${props => props.$variant === 'primary'
    ? 'white'
    : props.theme.colors.textSecondary};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[4]};
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[1]};
  font-size: 13px;
  color: ${props => props.theme.colors.textSecondary};

  strong {
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const Body = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borders.radius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing[4]};
  line-height: 1.6;
  font-size: 14px;
  color: ${props => props.theme.colors.textPrimary};

  pre {
    white-space: pre-wrap;
    margin: 0;
    font-family: inherit;
  }

  p {
    margin: ${props => props.theme.spacing[2]} 0;
  }

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

const Attachments = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundSecondary};
  font-size: 13px;
  color: ${props => props.theme.colors.textSecondary};

  .info {
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.spacing[1]};
  }

  strong {
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const Spinner = styled(Loader2)`
  width: 28px;
  height: 28px;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const AttachmentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px dashed ${props => props.theme.colors.border};
  background: transparent;
  font-size: 12px;
  color: ${props => props.theme.colors.textSecondary};
  cursor: not-allowed;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.error};
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const formatBytes = (bytes?: number) => {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(1)} ${units[unitIndex]}`;
};

const formatDate = (timestamp?: number) => {
  if (!timestamp) return '—';
  try {
    return format(new Date(Number(timestamp)), "dd MMM yyyy 'às' HH:mm", {
      locale: ptBR,
    });
  } catch (error) {
    console.error('[MessageViewer] Failed to parse date', error);
    return '—';
  }
};

const formatError = (error: unknown) => {
  if (!error) return 'Não foi possível carregar a mensagem selecionada.';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object') {
    const { response, message } = error as any;
    return (
      response?.data?.message ||
      message ||
      'Não foi possível carregar a mensagem selecionada.'
    );
  }
  return 'Não foi possível carregar a mensagem selecionada.';
};

export const MessageViewer: React.FC<MessageViewerProps> = ({
  messageId,
  message,
  isLoading,
  error,
  isTogglingRead = false,
  onReply,
  onForward,
  onToggleRead,
  onEnterFullscreen,
  onBack,
}) => {
  const isUnread = useMemo(() => {
    if (!message) return false;
    return Boolean(message.labelIds?.includes('UNREAD'));
  }, [message]);

  const filteredLabels = useMemo(() => {
    if (!message?.labelIds) return [];
    return message.labelIds.filter(
      label => label !== 'UNREAD' && label !== 'INBOX'
    );
  }, [message?.labelIds]);

  const handleToggleRead = async () => {
    if (!message || !onToggleRead) {
      return;
    }

    try {
      await onToggleRead(message, isUnread);
    } catch (err) {
      console.error('[MessageViewer] Failed to toggle read state', err);
    }
  };

  const handleReply = () => {
    if (!message || !onReply) return;
    onReply(message);
  };

  const handleForward = () => {
    if (!message || !onForward) return;
    onForward(message);
  };

  const handleEnterFullscreen = () => {
    if (!message || !onEnterFullscreen) return;
    onEnterFullscreen(message);
  };

  if (!messageId) {
    return (
      <Container>
        <Placeholder>
          <Eye />
          <div>Selecione uma mensagem para visualizar o conteúdo completo.</div>
        </Placeholder>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <Placeholder>
          <Spinner />
          <div>Carregando mensagem...</div>
        </Placeholder>
      </Container>
    );
  }

  if (error || !message) {
    return (
      <Container>
        <Placeholder>
          <AlertTriangle />
          <ErrorMessage>
            <AlertTriangle size={16} />
            <span>{formatError(error)}</span>
          </ErrorMessage>
        </Placeholder>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Subject>{message.subject || '(sem assunto)'}</Subject>
        <Meta>
          <span>
            <strong>De:</strong> {message.from || 'Remetente desconhecido'}
          </span>
          <span>
            <strong>Para:</strong> {message.to || '—'}
          </span>
          {message.cc && message.cc.length > 0 && (
            <span>
              <strong>CC:</strong> {message.cc.join(', ')}
            </span>
          )}
          {message.bcc && message.bcc.length > 0 && (
            <span>
              <strong>CCO:</strong> {message.bcc.join(', ')}
            </span>
          )}
          <span>
            <strong>Recebido:</strong> {formatDate(message.internalDate)}
          </span>
          {filteredLabels.map(label => (
            <Badge key={label}>
              <Tag size={12} />
              {label}
            </Badge>
          ))}
          {isUnread && (
            <Badge $variant="warning">
              <EyeOff size={12} />
              Não lida
            </Badge>
          )}
        </Meta>

        <ActionBar>
          {onBack && (
            <ActionButton type="button" onClick={onBack} title="Voltar" style={{ marginRight: 'auto', display: 'none' }} className="mobile-back-btn">
              <ArrowLeft />
            </ActionButton>
          )}
          <ActionButton type="button" onClick={handleReply} title="Responder">
            <Reply />
          </ActionButton>
          <ActionButton type="button" onClick={handleForward} title="Encaminhar">
            <Share />
          </ActionButton>
          <ActionButton type="button" onClick={handleEnterFullscreen} title="Tela Cheia">
            <Maximize />
          </ActionButton>
          <ActionButton
            type="button"
            $variant="primary"
            onClick={handleToggleRead}
            disabled={isTogglingRead}
            title={isUnread ? 'Marcar como lida' : 'Marcar como não lida'}
          >
            {isUnread ? <Eye /> : <EyeOff />}
          </ActionButton>
        </ActionBar>
      </Header>

      <style>{`
        @media (max-width: 768px) {
          .mobile-back-btn {
            display: inline-flex !important;
          }
        }
      `}</style>

      <Content>
        <InfoRow>
          <strong>ID da thread:</strong>
          <span>{message.threadId}</span>
        </InfoRow>

        <Body>
          {message.bodyHtml ? (
            <div dangerouslySetInnerHTML={{ __html: message.bodyHtml }} />
          ) : (
            <pre>{message.body || 'Mensagem sem conteúdo.'}</pre>
          )}
        </Body>

        {message.attachments && message.attachments.length > 0 && (
          <Attachments>
            <strong>Anexos ({message.attachments.length})</strong>
            {message.attachments.map((attachment: GmailAttachment) => (
              <AttachmentItem key={attachment.attachmentId}>
                <div className="info">
                  <strong>{attachment.filename || 'Anexo'}</strong>
                  <span>{formatBytes(attachment.size)}</span>
                </div>
                <AttachmentButton type="button" disabled>
                  <Download size={14} />
                  Baixar (em breve)
                </AttachmentButton>
              </AttachmentItem>
            ))}
          </Attachments>
        )}
      </Content>
    </Container>
  );
};

